import React from 'react'
import { get, set, cloneDeep, some, isEqual, sortBy, isNumber } from 'lodash'
import moment from 'moment-timezone'
import styled from 'styled-components'

import { palette, fonts, mediaQueries } from '../styles/variables'
import GoogleSignIn from '../components/buttons/GoogleSignIn'
import Tooltip from '../components/tooltip/Tooltip'

import { isSignedIn, getLoggedUser, getUserFreeTrialDays } from './userAuth'
import { translate } from './translation'
import { formatNumber } from './numbers'
import {
  KISSMETRICS_TRACK_EVENTS,
  PROPERTY_NAMES,
  PROPERTY_VALUES,
  kissmetricsRecordingEvent,
} from './kissmetrics'
import {
  addonMainLimitNames,
  getAddonTypeFromCode,
  getAddonByCode,
  getAddonByKey,
  getUserSubscription,
  getAvailableAddons,
  getAddonLimit,
} from './payment'
import { allAddonsTypes } from './addonTypes'
import { getGoogleSignInURL, getPricingURL } from './consts.mjs'
import { generateKey, getDomain } from './functions'
import { getDailyReportInfo } from './storage'
import { MIXPANEL_PROPS } from './mixpanel'
import { checkoutModalHandler } from './sendMessage'
import { Desktop } from './media'

export const GLOBAL = 'global'
export const KEYWORD_OVERVIEW = 'keyword_overview'
export const KEYWORD_IDEAS = 'keyword_ideas'
export const KEYWORD_VISUALIZATION = 'keyword_visualization'
export const KEYWORD_LISTS = 'keyword_lists'
export const CONTENT_IDEAS = 'content_ideas'
export const TRAFFIC_OVERVIEW = 'traffic_overview'
export const TOP_PAGES = 'top_pages'
export const TOP_KEYWORDS = 'top_keywords'
export const SITE_AUDIT = 'site_audit'
export const BACKLINKS = 'backlinks'
export const BACKLINK_OPPORTUNITY = 'backlink_opportunity'
export const DASHBOARD = 'dashboard'
export const SIMILAR_WEBSITES = 'similar_websites'
export const COMPETITOR_TRACKING = 'competitor_tracking'
export const RANK_TRACKING = 'rank_tracking'
export const WORKSPACE = 'workspace'
export const SEO_ANALYZER = 'seo_analyzer'
export const ALERTS = 'alerts'
export const LABS = 'labs'
export const KEYWORD_GENERATOR = 'keyword_generator'
export const SIDEBAR = 'sidebar'
export const AI_WRITER = 'ai_writer'
export const DISABLE_LIMIT_FEATURE =
  process.env.REACT_APP_DISABLE_LIMIT_FEATURE || false
export const FREE_USER_SD_LIMITS = 10
export const blockingConfig = {
  anonymous: {
    global: {
      reports_limits: 1, // Number of reports that user can perform per day.
      reset_interval: 24, // Reset Interval in hours. Reset at every day in PST
    },
    keyword_overview: {
      search_volume: true,
      people_click: true,
      age_range: true,
      actions: ['add_to_keyword_list'],
    },
    keyword_ideas: {
      suggestions: 15,
      domainKeywords: 20,
      related: 15,
      serp: 10,
      questions: 5,
      prepositions: 5,
      comparisons: 5,
      keywords: {
        suggestions: 140,
        related: 280,
        questions: 35,
        prepositions: 35,
        comparisons: 35,
      },
      actions: [
        'subscription',
        'serp_pagination',
        'add_to_keyword_list',
        'refresh_serp',
        'export',
        'copy',
        'load_more',
      ],
    },
    keyword_lists: {
      results: 3, // It is replaced with BE limits in getConfig function
      keywords: 30, // It is replaced with BE limits in getConfig function
      serp: 10,
      actions: [
        'pagination',
        'list_pagination',
        'serp_pagination',
        'add_to_rank_tracking',
        'export',
        'copy',
      ],
    },
    content_ideas: {
      results: 10,
      keywords: 5,
      backlinks: 5,
      actions: ['pagination', 'export', 'copy'],
    },
    traffic_overview: {
      traffic: true,
      seo_ranking: true,
    },
    top_pages: {
      results: 10,
      keywords: 5,
      backlinks: 5,
      actions: ['pagination', 'export', 'copy'],
    },
    top_keywords: {
      results: 20,
      serp: 10,
      actions: [
        'serp_pagination',
        'add_to_keyword_list',
        'refresh_serp',
        'export',
        'copy',
      ],
    },
    similar_websites: {
      results: 5,
      common_keywords: 10,
      keywords_gap: 10,
      traffic: true,
      actions: [
        'pagination',
        'more_domain',
        'common_keywords_export',
        'keywords_gap_export',
        'export',
        'copy',
      ],
    },
    site_audit: {
      all: 5,
      errors: 3,
      warnings: 3,
      recommendations: 3,
      issues: 5,
      page_crawled: 150,
      actions: [
        'recrawl',
        'export',
        'pages_discovered',
        'export_pages_discovered',
        'update_issue',
        'recrawl_issue',
      ],
    },
    backlinks: {
      results: 15,
      export_results: 15,
      new: 15,
      lost: 15,
      overtime: true,
      anchor_text: 10,
      actions: [
        'pagination',
        'copy',
        'new_lost_pagination',
        'anchor_pagination',
        'export',
      ],
    },
    backlink_opportunity: {
      refDomains: 10,
      backlinks: 10,
      actions: ['pagination', 'export'],
    },
    dashboard: {
      projects: 1,
      keywords: 25,
      locations: 1,
      update_frequency: 7,
      actions: [
        'rank_update_frequency_daily',
        'mobile_rank_tracking_on',
        'biweekly_call_yes',
        'seo_help_yes',
      ],
    },
    competitor_tracking: {
      common_keywords: 10,
      keywords_gap: 10,
      actions: ['pagination', 'common_keywords_export', 'keywords_gap_export'],
    },
    sidebar: {
      actions: [],
    },
    labs: {
      actions: ['toggle_ai_writer', 'toggle_keyword_visualization'],
    },
    keyword_generator: {
      actions: ['export'],
    },
    ai_writer: {
      actions: [
        'suggest_new',
        'save_document',
        'add_paragraph',
        'view_serp_result',
        'adjacent_topics',
        'start_scrapping',
      ],
    },
  },
  free: {
    global: {
      reports_limits: 3, // Number of reports that user can perform per day.
      reset_interval: 24, // Reset Interval in hours. Reset at every day in PST
    },
    keyword_overview: {
      search_volume: true,
      people_click: true,
      age_range: true,
    },
    keyword_ideas: {
      suggestions: 15,
      domainKeywords: 20,
      related: 15,
      serp: 10,
      questions: 5,
      prepositions: 5,
      comparisons: 5,
      actions: [
        'subscription',
        'serp_pagination',
        'refresh_serp',
        'copy',
        'export',
        'load_more',
      ],
      keywords: {
        suggestions: 140,
        related: 280,
        questions: 35,
        prepositions: 35,
        comparisons: 35,
      },
    },
    keyword_lists: {
      results: 3, // It is replaced with BE limits in getConfig function
      keywords: 30, // It is replaced with BE limits in getConfig function
      serp: 10,
      actions: [
        'pagination',
        'list_pagination',
        'serp_pagination',
        'export',
        'copy',
      ],
    },
    content_ideas: {
      results: 10,
      keywords: 5,
      backlinks: 5,
      actions: ['pagination', 'copy', 'export'],
    },
    traffic_overview: {
      traffic: true,
      seo_ranking: true,
    },
    top_pages: {
      results: 10,
      keywords: 5,
      backlinks: 5,
      actions: ['pagination', 'copy', 'export'],
    },
    top_keywords: {
      results: 20,
      serp: 10,
      actions: ['serp_pagination', 'refresh_serp', 'copy', 'export'],
    },
    similar_websites: {
      results: 5,
      common_keywords: 10,
      keywords_gap: 10,
      traffic: true,
      actions: [
        'pagination',
        'more_domain',
        'common_keywords_export',
        'keywords_gap_export',
        'copy',
        'export',
      ],
    },
    site_audit: {
      all: 5,
      errors: 3,
      warnings: 3,
      recommendations: 3,
      issues: 5,
      page_crawled: 150,
      actions: [
        'recrawl',
        'export',
        'export_pages_discovered',
        'update_issue',
        'recrawl_issue',
      ],
    },
    backlinks: {
      results: 15,
      export_results: 15,
      new: 15,
      lost: 15,
      overtime: true,
      anchor_text: 10,
      actions: [
        'pagination',
        'copy',
        'new_lost_pagination',
        'anchor_pagination',
        'export',
      ],
    },
    backlink_opportunity: {
      refDomains: 10,
      backlinks: 10,
      actions: ['pagination', 'export'],
    },
    dashboard: {
      projects: 1,
      keywords: 25,
      locations: 1,
      update_frequency: 7,
      actions: [
        'rank_update_frequency_daily',
        'mobile_rank_tracking_on',
        'biweekly_call_yes',
        'seo_help_yes',
      ],
    },
    competitor_tracking: {
      common_keywords: 10,
      keywords_gap: 10,
      actions: ['pagination', 'common_keywords_export', 'keywords_gap_export'],
    },
    sidebar: {
      actions: [],
    },
    labs: {
      actions: ['toggle_ai_writer', 'toggle_keyword_visualization'],
    },
    keyword_generator: {
      actions: ['export'],
    },
    ai_writer: {
      actions: [
        'suggest_new',
        'add_paragraph',
        'view_serp_result',
        'adjacent_topics',
      ],
    },
    rank_tracking: {
      actions: ['export'],
    },
  },
  tier0: {
    global: {
      reports_limits: 100, // Number of reports that user can perform per day.
      reset_interval: 24, // Reset Interval in hours. Reset at every day in PST
    },
    keyword_overview: {
      search_volume: false,
      people_click: false,
      age_range: false,
    },
    keyword_ideas: {
      related: 20000,
      export_related: 2000,
      domainKeywords: 2000,
      actions: [],
    },
    keyword_lists: {
      results: 3, // It is replaced with BE limits in getConfig function
      keywords: 1000, // It is replaced with BE limits in getConfig function
      actions: [],
    },
    content_ideas: {
      results: 2000,
      keywords: 2000,
      backlinks: 2000,
      actions: [],
    },
    traffic_overview: {
      traffic: false,
      seo_ranking: false,
    },
    top_pages: {
      results: 2000,
      keywords: 2000,
      backlinks: 2000,
      actions: [],
    },
    top_keywords: {
      actions: [],
    },
    similar_websites: {
      results: 20,
      actions: [],
    },
    site_audit: {
      page_crawled: 1000,
      actions: [],
    },
    backlinks: {
      results: 100,
      export_results: 100,
      anchor_text: 10,
      new: 100,
      lost: 100,
      actions: ['pagination'],
    },
    dashboard: {
      projects: 1,
      keywords: 25,
      locations: 1,
      update_frequency: 7,
      actions: ['rank_update_frequency_daily', 'mobile_rank_tracking_on'],
    },
    sidebar: {
      actions: [],
    },
  },
  tier1: {
    global: {
      reports_limits: 100, // Number of reports that user can perform per day.
      reset_interval: 24, // Reset Interval in hours. Reset at every day in PST
    },
    keyword_overview: {
      search_volume: false,
      people_click: false,
      age_range: false,
    },
    keyword_ideas: {
      related: 20000,
      export_related: 2000,
      domainKeywords: 2000,
      actions: [],
    },
    keyword_lists: {
      keywords: 1000, // It is replaced with BE limits in getConfig function
    },
    content_ideas: {
      results: 2000,
      keywords: 2000,
      backlinks: 2000,
      actions: [],
    },
    traffic_overview: {
      traffic: false,
      seo_ranking: false,
    },
    top_pages: {
      results: 2000,
      keywords: 2000,
      backlinks: 2000,
      actions: [],
    },
    top_keywords: {
      results: 2000,
      actions: [],
    },
    similar_websites: {
      results: 20,
      actions: [],
    },
    site_audit: {
      page_crawled: 1000,
      actions: [],
    },
    backlinks: {
      results: 2000,
      export_results: 2000,
      actions: [],
    },
    dashboard: {
      projects: 3,
      keywords: 100,
      locations: 20,
      update_frequency: 1,
    },
    sidebar: {
      actions: [],
    },
  },
  tier2: {
    global: {
      reports_limits: 300, // Number of reports that user can perform per day.
      reset_interval: 24, // Reset Interval in hours. Reset at every day in PST
    },
    keyword_overview: {
      search_volume: false,
      people_click: false,
      age_range: false,
    },
    keyword_ideas: {
      related: 50000,
      export_related: 5000,
      domainKeywords: 5000,
      actions: [],
    },
    keyword_lists: {
      keywords: 1000, // It is replaced with BE limits in getConfig function
    },
    content_ideas: {
      results: 5000,
      keywords: 5000,
      backlinks: 5000,
      actions: [],
    },
    traffic_overview: {
      traffic: false,
      seo_ranking: false,
    },
    top_pages: {
      results: 5000,
      keywords: 5000,
      backlinks: 5000,
      actions: [],
    },
    top_keywords: {
      actions: [],
    },
    similar_websites: {
      results: 20,
      actions: [],
    },
    site_audit: {
      page_crawled: 10000,
      actions: [],
    },
    backlinks: {
      results: 5000,
      export_results: 5000,
      actions: [],
    },
    dashboard: {
      projects: 15,
      keywords: 200,
      locations: 200,
      update_frequency: 1,
    },
    sidebar: {
      actions: [],
    },
  },
  tier3: {
    global: {
      reports_limits: 900, // Number of reports that user can perform per day.
      reset_interval: 24, // Reset Interval in hours. Reset at every day in PST
    },
    backlinks: {
      results: 10000,
      export_results: 10000,
    },
    keyword_ideas: {
      related: 100000,
      export_related: 10000,
      domainKeywords: 10000,
    },
    content_ideas: {
      results: 10000,
      keywords: 10000,
      backlinks: 10000,
      actions: [],
    },
    top_pages: {
      results: 10000,
      keywords: 10000,
      backlinks: 10000,
      actions: [],
    },
  },
  restart: {
    global: {
      reports_limits: 50, // Number of reports that user can perform per day.
      reset_interval: 24, // Reset Interval in hours. Reset at every day in PST
    },
    keyword_overview: {
      search_volume: false,
      people_click: false,
      age_range: false,
    },
    keyword_ideas: {
      related: 20000,
      export_related: 2000,
      domainKeywords: 2000,
      actions: [],
    },
    keyword_lists: {
      results: 3, // It is replaced with BE limits in getConfig function
      keywords: 1000, // It is replaced with BE limits in getConfig function
      actions: [],
    },
    content_ideas: {
      results: 2000,
      keywords: 2000,
      backlinks: 2000,
      actions: [],
    },
    traffic_overview: {
      traffic: false,
      seo_ranking: false,
    },
    top_pages: {
      results: 2000,
      keywords: 2000,
      backlinks: 2000,
      actions: [],
    },
    top_keywords: {
      results: 2000,
      actions: [],
    },
    similar_websites: {
      results: 20,
      actions: [],
    },
    site_audit: {
      page_crawled: 500,
      actions: [],
    },
    backlinks: {
      results: 100,
      export_results: 100,
      anchor_text: 10,
      new: 100,
      lost: 100,
      actions: ['pagination', 'anchor_pagination'],
    },
    dashboard: {
      projects: 1,
      keywords: 50,
      locations: 20,
      update_frequency: 7,
      actions: ['rank_update_frequency_daily', 'mobile_rank_tracking_on'],
    },
    sidebar: {
      actions: [],
    },
  },
}

export const getConfig = (tier) => {
  const config = blockingConfig[tier]
  const userLimits = getMaxValuesForUser()

  set(config, [KEYWORD_LISTS, 'results'], userLimits.MAX_KEYWORD_LISTS_PER_USER)
  set(config, [KEYWORD_LISTS, 'keywords'], userLimits.MAX_KEYWORDS_PER_LIST)

  return config
}

const BlockWrapper = styled.div((props) => ({
  position: 'absolute',
  pointerEvents: 'none',
  cursor: 'default !important',
  fontSize: 'inherit',
  color: `${palette.darkGray50} !important`,
  width: props.width || '100%',
  right: props.width !== '100%' ? 0 : 'inherit',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent:
    props.height === 'auto' || props.height > 800 ? 'flex-start' : 'center',
  paddingTop: props.height === 'auto' || props.height > 800 ? '200px' : '0',
  height: props.height,
  zIndex: 1,
  [mediaQueries.mobile]: {
    position: props.position || 'absolute',
    minWidth: '210px',
  },
}))

const ChartBlockWrapper = styled.div((props) => ({
  position: 'absolute',
  pointerEvents: 'none',
  cursor: 'default !important',
  fontSize: 'inherit',
  color: `${palette.lightGray20} !important`,
  width: props.width || '40%',
  height: props.height || '60%',
  // minWidth: '500px',
  left: props.left || 60,
  top: props.top || 50,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent:
    props.height === 'auto' || props.height > 800 ? 'flex-start' : 'center',
  zIndex: 1,
  [mediaQueries.mobile]: {
    position: props.position || 'absolute',
    minWidth: '180px',
  },
}))

const BlockContent = styled.div({
  width: '100%',
  height: '100%',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
})

const BlurBackground = styled.div((props) => ({
  position: 'absolute',
  width: '100%',
  height: '100%',
  top: 0,
  left: 0,
  background: props.enabled ? palette.white : palette.transparent,
  filter: 'blur(9px)',
  '-webkit-filter': 'blur(9px)',
  zIndex: -1,
  boxShadow: props.enabled
    ? 'inset 1px -1px 90px 57px rgb(237 99 52 / 13%)'
    : 'none',
}))

const BlockMessage = styled.div({
  fontSize: '14px',
  fontFamily: fonts.primaryBold,
  letterSpacing: '1px',
  color: palette.darkGray,
  whiteSpace: 'pre-wrap',
  marginBottom: '28px',
  textTransform: 'uppercase',
  textAlign: 'center',
  pointerEvents: 'all',
  [mediaQueries.mobile]: {
    marginBottom: '10px',
    fontSize: '12px',
  },
})

export const MobileSticky = styled.div(({ top = 100 }) => ({
  position: 'sticky',
  height: 0,
  top,
  zIndex: 1,
}))

export const BlockFeatureAction = (
  page,
  feature,
  value,
  nextFunc,
  blockFunc,
) => {
  if (!shouldBlockFeature(page, feature, value)) return nextFunc
  return blockFunc
}

const getFeatureName = (page, feature) => {
  if (feature === 'results') {
    return page
  }
  if (feature === 'related') {
    return 'related_keywords'
  }
  if (feature === 'all') {
    return 'errors_and_suggestions'
  }
  return feature
}

const getButtonLabel = () => {
  const isLoggedIn = isSignedIn()
  const freeTrialDays = getUserFreeTrialDays()

  if (!isLoggedIn) {
    return translate('sign_in_with_google')
  }

  if (isPaidUser()) {
    return translate('upgrade')
  }

  return translate('seven_day_free', freeTrialDays)
}

export const BlockFeatureWrapper = (
  page,
  feature,
  totalCount,
  ref,
  btnLocation,
  width = '100%',
  height = '100%',
  lang = 'en',
  nextPage = '/',
  ctaTooltip = null,
  showPayPlans = true,
  position = 'absolute',
  skipTier0User = true,
  filteredCount = -1,
) => {
  if (skipTier0User) return false
  const isLoggedIn = isSignedIn()
  const freeTrialDays = getUserFreeTrialDays()

  let message
  let buttonRedirectURL = isLoggedIn
    ? getPricingURL({
        next: nextPage,
        lang,
        showPayPlans,
      })
    : getGoogleSignInURL({
        next: nextPage,
        lang,
        event_id: crypto.randomUUID(),
      })
  let buttonLabel = getButtonLabel()
  let hideIcon = isLoggedIn

  switch (page) {
    case KEYWORD_OVERVIEW:
      message = translate(
        'upgrade_to_more_keywords_heading',
        formatNumber(getblockCount(KEYWORD_IDEAS, feature, totalCount)),
      )
      buttonLabel = isPaidUser()
        ? translate('upgrade')
        : translate('seven_day_free', freeTrialDays)
      hideIcon = true
      buttonRedirectURL = getPricingURL({
        next: nextPage,
        lang,
        showPayPlans,
      })
      break
    case KEYWORD_IDEAS:
      if (feature === 'serp') {
        message = translate('upgrade_to_more_serp_heading')
      } else if (feature === 'domainKeywords') {
        message = translate(
          'upgrade_to_see_keywords_heading',
          formatNumber(10000),
        )
      } else {
        message = translate(
          'upgrade_to_more_keywords_heading',
          formatNumber(
            getblockCount(page, feature, totalCount, false, filteredCount),
          ),
        )
      }
      buttonLabel = isPaidUser()
        ? translate('upgrade')
        : translate('seven_day_free', freeTrialDays)
      hideIcon = true
      buttonRedirectURL = getPricingURL({
        next: nextPage,
        lang,
        showPayPlans,
      })
      break
    case SITE_AUDIT:
      if (feature === 'issues') {
        message = translate(
          'upgrade_audit_seo_error',
          formatNumber(
            getblockCount(page, feature, totalCount),
            false,
            0,
            null,
            true,
          ),
        )
        buttonLabel = isPaidUser()
          ? translate('upgrade')
          : translate('seven_day_free', freeTrialDays)
        hideIcon = true
        buttonRedirectURL = getPricingURL({
          next: nextPage,
          lang,
          showPayPlans,
        })
      } else if (feature === 'passed') {
        message = translate('upgrade_audit_seo_checks_passed')
        buttonLabel = translate('seven_day_free', freeTrialDays)
        hideIcon = true
        buttonRedirectURL = getPricingURL({
          next: nextPage,
          lang,
          showPayPlans,
        })
      } else {
        message = translate('upgrade_audit_all_error')
        buttonLabel = translate('seven_day_free', freeTrialDays)
        hideIcon = true
        buttonRedirectURL = getPricingURL({
          next: nextPage,
          lang,
          showPayPlans,
        })
      }

      break
    case CONTENT_IDEAS:
      if (feature === 'keywords') {
        message = translate(
          'upgrade_to_see_general_heading',
          formatNumber(
            getblockCount(page, feature, totalCount),
            false,
            0,
            null,
            true,
          ),
          translate('keywords').toLowerCase(),
        )
      } else if (feature === 'backlinks') {
        message = translate('upgrade_backlinks_heading')
      } else {
        message = translate('upgrade_more_content_ideas_heading')
      }
      buttonLabel = isPaidUser()
        ? translate('upgrade')
        : translate('seven_day_free', freeTrialDays)
      hideIcon = true
      buttonRedirectURL = getPricingURL({
        next: nextPage,
        lang,
        showPayPlans,
      })
      break
    case TOP_KEYWORDS:
      if (feature === 'serp') {
        message = translate('upgrade_to_more_serp_heading')
      } else {
        message = translate(
          'upgrade_to_see_keywords_heading',
          formatNumber(10000, false, 0, null, true),
        )
      }
      buttonLabel = isPaidUser()
        ? translate('upgrade')
        : translate('seven_day_free', freeTrialDays)
      hideIcon = true
      buttonRedirectURL = getPricingURL({
        next: nextPage,
        lang,
        showPayPlans,
      })
      break
    case TOP_PAGES:
      if (feature === 'results') {
        message = translate('upgrade_more_top_pages_heading')
      } else if (feature === 'keywords') {
        message = translate(
          'upgrade_to_see_general_heading',
          formatNumber(
            getblockCount(page, feature, totalCount),
            false,
            0,
            null,
            true,
          ),
          translate('keywords').toLowerCase(),
        )
      } else {
        message = translate('upgrade_backlinks_heading')
      }
      buttonLabel = isPaidUser()
        ? translate('upgrade')
        : translate('seven_day_free', freeTrialDays)
      hideIcon = true
      buttonRedirectURL = getPricingURL({
        next: nextPage,
        lang,
        showPayPlans,
      })
      break
    case COMPETITOR_TRACKING:
    case SIMILAR_WEBSITES:
      if (feature === 'common_keywords') {
        message = translate(
          'upgrade_to_see_general_heading',
          formatNumber(
            getblockCount(page, feature, totalCount),
            false,
            0,
            null,
            true,
          ),
          translate('common_keywords').toLowerCase(),
        )
      } else if (feature === 'keywords_gap') {
        message = translate(
          'upgrade_to_see_general_heading',
          formatNumber(
            getblockCount(page, feature, totalCount),
            false,
            0,
            null,
            true,
          ),
          translate('keywords_gap').toLowerCase(),
        )
      } else {
        message = translate(
          'upgrade_to_see_general_heading',
          formatNumber(
            getblockCount(page, feature, totalCount),
            false,
            0,
            null,
            true,
          ),
          translate('competing_domains'),
        )
      }
      buttonLabel = isPaidUser()
        ? translate('upgrade')
        : translate('seven_day_free', freeTrialDays)
      hideIcon = true
      buttonRedirectURL = getPricingURL({
        next: nextPage,
        lang,
        showPayPlans,
      })
      break
    case BACKLINKS:
      if (feature === 'new_lost') {
        message = translate(
          'upgrade_prompt_headline_no_count',
          translate('new_lost_referral_domains'),
        )
        buttonLabel = isPaidUser()
          ? translate('upgrade')
          : translate('seven_day_free', freeTrialDays)
        hideIcon = true
        buttonRedirectURL = getPricingURL({
          next: nextPage,
          lang,
          showPayPlans,
        })
      } else if (feature === 'anchor_text') {
        message = translate(
          'anchor_text_upgrade_block',
          formatNumber(
            getblockCount(page, feature, totalCount),
            false,
            0,
            null,
            true,
          ),
        )
        buttonLabel = isPaidUser()
          ? translate('upgrade')
          : translate('seven_day_free', freeTrialDays)
        hideIcon = true
        buttonRedirectURL = getPricingURL({
          next: nextPage,
          lang,
          showPayPlans,
        })
      } else {
        message = translate(
          'upgrade_to_see_backlinks',
          formatNumber(
            totalCount - getUnblockCount(BACKLINKS, 'results', totalCount),
          ),
        )
        buttonLabel = isPaidUser()
          ? translate('upgrade')
          : translate('seven_day_free', freeTrialDays)
        hideIcon = true
        buttonRedirectURL = getPricingURL({
          next: nextPage,
          lang,
          showPayPlans,
        })
      }

      break
    case BACKLINK_OPPORTUNITY:
      if (feature === 'refDomains') {
        message = translate('upgrade_backlink_opportunities')
        buttonLabel = translate('seven_day_free', freeTrialDays)
        hideIcon = true
        buttonRedirectURL = getPricingURL({
          next: nextPage,
          lang,
          showPayPlans,
        })
      } else if (feature === 'backlinks') {
        message = translate('upgrade_backlink_opportunities')
        buttonLabel = translate('seven_day_free', freeTrialDays)
        hideIcon = true
        buttonRedirectURL = getPricingURL({
          next: nextPage,
          lang,
          showPayPlans,
        })
      } else {
        message = translate(
          isLoggedIn
            ? 'upgrade_to_see_general_heading'
            : 'login_to_see_general_heading',
          formatNumber(
            getblockCount(page, feature, totalCount),
            false,
            0,
            null,
            true,
          ),
          translate(getFeatureName(page, feature)),
        )
      }
      break
    case KEYWORD_LISTS:
      message = translate(
        isLoggedIn
          ? 'upgrade_to_see_general_heading'
          : 'login_to_see_general_heading',
        formatNumber(
          getblockCount(page, feature, totalCount),
          false,
          0,
          null,
          true,
        ),
        translate('menu_keyword_lists'),
      )
      break
    default:
      message = translate(
        isLoggedIn
          ? 'upgrade_to_see_general_heading'
          : 'login_to_see_general_heading',
        formatNumber(
          getblockCount(page, feature, totalCount),
          false,
          0,
          null,
          true,
        ),
        translate(getFeatureName(page, feature)),
      )
      break
  }
  return (
    <BlockWrapper
      data-testid="upgrade-block"
      ref={ref}
      width={width}
      height={height}
      position={position}
    >
      <BlockMessage data-testid="upgrade-message">
        {message}
        {ctaTooltip && (
          <Tooltip
            id={`${page}_${feature}_tooltip`}
            text={ctaTooltip}
            style={{ marginLeft: '10px' }}
            place="top-end"
          />
        )}
      </BlockMessage>
      <GoogleSignIn
        data-testid="upgrade-button"
        redirectURL={buttonRedirectURL}
        hideIcon={hideIcon}
        label={buttonLabel}
        height="42px"
        theme="dark"
        onClick={() => {
          if (PROPERTY_VALUES[page] && PROPERTY_VALUES[page][feature]) {
            const propertyValue = PROPERTY_VALUES[page][feature]
            if (hideIcon) {
              kissmetricsRecordingEvent(
                KISSMETRICS_TRACK_EVENTS.clicked_upgrade,
                {
                  [PROPERTY_NAMES.btn_location]: propertyValue,
                },
              )
            } else {
              kissmetricsRecordingEvent(KISSMETRICS_TRACK_EVENTS.clicked_siwg, {
                [PROPERTY_NAMES.btn_location]: propertyValue,
              })
            }
          }
          if (btnLocation && buttonLabel !== translate('sign_in_with_google')) {
            const mixpanelProps = [
              btnLocation,
              buttonLabel === translate('upgrade')
                ? MIXPANEL_PROPS.BUTTON_CTA.values.UPGRADE
                : MIXPANEL_PROPS.BUTTON_CTA.values.START_TRIAL,
            ]

            checkoutModalHandler({
              mixpanelProps,
            })
          }
        }}
        noRedirect
      />
    </BlockWrapper>
  )
}

export const BlockChartWrapper = (
  page,
  chart,
  width,
  height,
  left,
  top,
  lang = 'en',
  nextPage = '/',
  ctaTooltip = null,
  translationKey = null,
  buttonHeight = null,
) => {
  if (!shouldBlockChart(page, chart)) return false

  let message
  const buttonRedirectURL = getPricingURL({
    next: nextPage,
    lang,
  })
  const buttonLabel = translate('seven_day_free', getUserFreeTrialDays())
  let onClickHandler = null

  switch (page) {
    case KEYWORD_OVERVIEW:
      if (chart === 'search_volume') {
        message = translate('upgrade_keywords_heading')
        onClickHandler = () => {
          kissmetricsRecordingEvent(KISSMETRICS_TRACK_EVENTS.clicked_upgrade, {
            [PROPERTY_NAMES.btn_location]:
              PROPERTY_VALUES[KEYWORD_OVERVIEW].volume_chart,
          })
        }
      }
      break
    case TRAFFIC_OVERVIEW:
      if (chart === 'traffic') {
        message = translate(translationKey || 'upgrade_traffic_analyzer2')
        onClickHandler = () => {
          kissmetricsRecordingEvent(KISSMETRICS_TRACK_EVENTS.clicked_upgrade, {
            [PROPERTY_NAMES.btn_location]:
              PROPERTY_VALUES[TRAFFIC_OVERVIEW].traffic_chart,
          })
        }
      } else if (chart === 'seo_ranking') {
        message = translate('upgrade_traffic_analyzer1')
        onClickHandler = () => {
          kissmetricsRecordingEvent(KISSMETRICS_TRACK_EVENTS.clicked_upgrade, {
            [PROPERTY_NAMES.btn_location]:
              PROPERTY_VALUES[TRAFFIC_OVERVIEW].ranking_chart,
          })
        }
      }
      break
    case BACKLINKS:
      if (chart === 'overtime') {
        message = translate('upgrade_backlinks_overtime')
      } else if (chart === 'new_lost') {
        message = translate('upgrade_backlinks_overtime')
      }
      break
    case SIMILAR_WEBSITES:
      if (chart === 'traffic') {
        message = translate('upgrade_competitors_traffic')
        onClickHandler = () => {
          kissmetricsRecordingEvent(KISSMETRICS_TRACK_EVENTS.clicked_upgrade, {
            [PROPERTY_NAMES.btn_location]:
              PROPERTY_VALUES[SIMILAR_WEBSITES].traffic_chart,
          })
        }
      }
      break
    default:
      message = translate('login_keywords_heading')
      break
  }
  return (
    <ChartBlockWrapper width={width} height={height} left={left} top={top}>
      <BlockContent>
        <BlurBackground />
        <BlockMessage>
          {message}
          {ctaTooltip && (
            <Tooltip
              id={`${page}_${chart}_tooltip`}
              text={ctaTooltip}
              style={{ marginLeft: '10px' }}
            />
          )}
        </BlockMessage>
        <GoogleSignIn
          useClickEvent
          redirectURL={buttonRedirectURL}
          hideIcon
          label={buttonLabel}
          height={buttonHeight || '42px'}
          theme="dark"
          onClick={() => {
            onClickHandler?.()
            const mixpanelProps = [
              MIXPANEL_PROPS.PURCHASE_BUTTON_LOCATION.values.BLOCK_OFF_DATA,
              MIXPANEL_PROPS.BUTTON_CTA.values.START_TRIAL,
            ]
            checkoutModalHandler({
              mixpanelProps,
            })
          }}
          noRedirect
        />
      </BlockContent>
    </ChartBlockWrapper>
  )
}

export const BlockHortizontalChartWrapper = (
  page,
  chart,
  width,
  height,
  left,
  top,
  lang = 'en',
  nextPage = '/',
  ctaTooltip = null,
) => {
  if (!shouldBlockChart(page, chart)) return false

  let message
  const buttonRedirectURL = getPricingURL({
    next: nextPage,
    lang,
  })
  const buttonLabel = translate('seven_day_free', getUserFreeTrialDays())
  let onClickHandler = () => ({})

  if (chart === 'people_click') {
    message = translate('upgrade_to_see_people_click_chart')
    onClickHandler = () => {
      kissmetricsRecordingEvent(KISSMETRICS_TRACK_EVENTS.clicked_upgrade, {
        [PROPERTY_NAMES.btn_location]:
          PROPERTY_VALUES.keyword_overview.seo_chart,
      })
    }
  } else if (chart === 'age_range') {
    message = translate('upgrade_to_see_age_range_chart')
    onClickHandler = () => {
      kissmetricsRecordingEvent(KISSMETRICS_TRACK_EVENTS.clicked_upgrade, {
        [PROPERTY_NAMES.btn_location]:
          PROPERTY_VALUES.keyword_overview.age_range_chart,
      })
    }
  }
  return (
    <ChartBlockWrapper
      width={width}
      height={height}
      left={left}
      top={top}
      style={{
        flexDirection: 'row',
        justifyContent: 'space-between',
        padding: '0 10px',
      }}
    >
      <Desktop>
        <BlurBackground enabled />
        <BlockContent
          style={{ flexDirection: 'row', justifyContent: 'flex-start' }}
        >
          <BlockMessage
            style={{
              marginBottom: '0px',
              textTransform: 'none',
              fontFamily: fonts.primary,
              letterSpacing: 0,
              marginRight: '30px',
              textAlign: 'left',
            }}
          >
            {message}
            {ctaTooltip && (
              <Tooltip
                id={`${page}_${chart}_tooltip`}
                text={ctaTooltip}
                style={{ marginLeft: '10px' }}
              />
            )}
          </BlockMessage>
        </BlockContent>
      </Desktop>
      <GoogleSignIn
        redirectURL={buttonRedirectURL}
        hideIcon
        label={buttonLabel}
        height="42px"
        theme="dark"
        style={{ height: '30px', padding: '0 10px', boxShadow: 'none' }}
        onClick={() => {
          onClickHandler?.()
          const mixpanelProps = [
            MIXPANEL_PROPS.PURCHASE_BUTTON_LOCATION.values.BLOCK_OFF_DATA,
            MIXPANEL_PROPS.BUTTON_CTA.values.START_TRIAL,
          ]
          checkoutModalHandler({ mixpanelProps })
        }}
        noRedirect
      />
    </ChartBlockWrapper>
  )
}

export const shouldBlockChart = (page, chart, disableBlockFeature = false) => {
  if (disableBlockFeature) return false
  const userTier = getUserTier()
  const config = getConfig(userTier)
  return get(config, [page, chart], false)
}

export const isLimitDisabled = () => DISABLE_LIMIT_FEATURE === 'true'

export const shouldBlockFeature = (
  page,
  feature,
  position,
  disableBlockFeature = false,
) => {
  if (disableBlockFeature) return false

  const { MAX_PROJECTS_PER_USER } = getMaxValuesForUser()

  if (page === DASHBOARD) {
    if (feature === 'new_project') {
      return position >= MAX_PROJECTS_PER_USER
    }
    if (feature === 'actions') {
      const userTier = getUserTier()
      const config = getConfig(userTier)
      return get(config, [page, feature], '').indexOf(position) > -1
    }
  }

  if (page === SIDEBAR) {
    if (feature === 'new_project') {
      return position >= MAX_PROJECTS_PER_USER
    }
  }

  let userTier = getUserTier()

  if (isLimitDisabled()) {
    userTier = 'tier1'
  }

  const config = getConfig(userTier)
  if (feature === 'actions') {
    return get(config, [page, feature], '').indexOf(position) > -1
  }
  return position >= get(config, [page, feature], position + 1)
}

export const checkLimitsSearch = (type) => {
  try {
    const reportTypes = [
      {
        used: 'reports_used',
        limit: 'reports_limits',
      },
    ]
    const dailyReport = JSON.parse(localStorage.getItem('daily_report'))
    const { used, limit } = reportTypes.find((item) => item.used === type)

    // Make sure type is one of reportTypes and we have `daily_report` localstorage
    if (!used || !limit || !dailyReport) return false

    if (isNumber(dailyReport[used]) && isNumber(dailyReport[limit]))
      return dailyReport[limit] - dailyReport[used] <= 0
    return false
  } catch (error) {
    return false
  }
}

export const shouldBlockSearch = (
  search,
  locId,
  lang,
  domainType = undefined,
  competitors = [],
  searchType = '',
) => {
  const reportsUsed = searchType ? `${searchType}_reports_used` : 'reports_used'
  const reportsLimits = searchType
    ? `${searchType}_reports_limits`
    : 'reports_limits'

  let limitInfo = getDailyReportInfo(searchType)
  if (isPaidUser()) {
    if (limitInfo[reportsUsed] >= limitInfo[reportsLimits]) return true
    return false
  }

  resetGlobalSearch(searchType)
  limitInfo = getDailyReportInfo(searchType)

  const globalSearch = getGlobalSearch(searchType)
  if (domainType !== 'keyword') search = getDomain(search, domainType)
  const hasSearch = globalSearch.list.some((item) => {
    return (
      generateKey([search, locId, lang, domainType]) ===
        generateKey([item.search, item.locId, item.lang, item.domainType]) &&
      (competitors.length === 0 || isEqual(item.competitors, competitors))
    )
  })

  if (!hasSearch && checkLimitsSearch(reportsUsed)) return true
  if (!hasSearch && limitInfo[reportsLimits] <= limitInfo[reportsUsed])
    return true
  return false
}

export const shouldShowDailyLimitMessage = () => {
  if (isPaidUser()) {
    const limitInfo = getDailyReportInfo()
    if (limitInfo.reports_used >= 0.9 * limitInfo.reports_limits) return true
    return false
  }
  return false
}

const resetGlobalSearch = (type) => {
  const globalSearch = getGlobalSearch(type)
  const now = moment().tz('America/Los_Angeles')
  const resetTime = moment(globalSearch.resetTime).tz('America/Los_Angeles')
  const userTier = getUserTier()
  const config = getConfig(userTier)
  const resetInterval = get(config, ['global', 'reset_interval'])
  const globalField = `global_search${type ? `_${type}` : ''}`
  if (now.diff(resetTime, 'hours') >= resetInterval) {
    localStorage.setItem(
      globalField,
      JSON.stringify({
        resetTime: now.startOf('day').format(),
        list: [],
      }),
    )
  }
}

export const saveSearch = (
  search,
  locId,
  lang,
  domainType = undefined,
  competitors = [],
  searchType = '',
  callback = null,
) => {
  if (isPaidUser()) {
    if (callback) callback()
    return
  }
  const globalSearch = getGlobalSearch(searchType)
  if (domainType !== 'keyword') search = getDomain(search, domainType)

  const newSearch = {
    search,
    locId: `${locId}`,
    lang,
    domainType,
    competitors: competitors ? sortBy(competitors, ['domain', 'mode']) : [],
  }
  if (
    !globalSearch.list.some((item) => {
      return (
        generateKey([search, locId, lang, domainType]) ===
          generateKey([item.search, item.locId, item.lang, item.domainType]) &&
        (competitors.length === 0 || isEqual(item.competitors, competitors))
      )
    })
  ) {
    globalSearch.list.push(newSearch)
  }
  const globalField = `global_search${searchType ? `_${searchType}` : ''}`
  localStorage.setItem(globalField, JSON.stringify(globalSearch))
  if (callback) callback()
}

export const getGlobalSearch = (type = '') => {
  const globalField = `global_search${type ? `_${type}` : ''}`
  const str = localStorage.getItem(globalField)
  if (str) {
    return JSON.parse(str)
  }
  return {
    resetTime: moment().tz('America/Los_Angeles').startOf('day').format(),
    list: [],
  }
}

export const getGlobalSearchLimit = (type = '') => {
  const reportLimits = `${type ? `${type}_` : ''}reports_limits`
  const userTier = getUserTier()
  const config = getConfig(userTier)
  const globalSearchLimit = get(config, ['global', reportLimits])

  return globalSearchLimit
}

export const isPaidUser = (user = getLoggedUser()) => {
  if (!isSignedIn()) return false
  if (
    user.subscription &&
    (user.subscription.subscriptionStatus === 'active' ||
      user.subscription.subscriptionStatus === 'trialing' ||
      user.subscription.subscriptionStatus === 'paid' ||
      user.subscription.subscriptionStatus === 'canceled' ||
      isUserInGracePeriod(user))
  )
    return true
  return false
}

export const isFreeTrialUser = (user = getLoggedUser()) => {
  if (!isSignedIn()) return false
  return user?.subscription?.subscriptionStatus === 'trialing'
}

export const isFreeTrialUsed = () => {
  const user = getLoggedUser()
  if (user?.free_trial_used) return true
  return false
}

export const freeTrialDaysLeft = () => {
  const user = getLoggedUser()
  if (user?.free_trial_used) {
    const current = Date.now()
    const trialStarted = user.free_trial_used * 1000
    if (current > trialStarted + 7 * 24 * 60 * 60 * 1000) {
      return 0
    }
    return Math.floor((current - trialStarted) / (1000 * 60 * 60 * 24))
  }
  return 7
}

export const isInFreeTrialPeriod = () => {
  const user = getLoggedUser()
  return (
    user?.free_trial_used &&
    Date.now() - user.free_trial_used * 1000 < 7 * 24 * 60 * 60 * 1000
  )
}

export const isLifetimePlanUser = () => {
  if (!isSignedIn()) return false

  const user = getLoggedUser()

  if (user.subscription && user.subscription.planInterval === 'lifetime')
    return true
  return false
}

export const getUserTier = () => {
  if (!isSignedIn()) return 'anonymous'
  const user = getLoggedUser()
  if (!user.subscription) return 'free'
  return user.subscription.tier
}

// Pro user is tier1 2 and 3 users
export const isProUser = () => isPaidUser() && getUserTier() !== 'tier0'

export const getMaxValuesForUser = () => {
  let maxAvailableAddonProject = 0

  if (isSignedIn()) {
    const user = getLoggedUser()

    if (user.subscription) {
      const availableAddons = getAvailableAddons()
      const subscription = getUserSubscription()
      const purchasedAddons = subscription?.addOns

      if (availableAddons && purchasedAddons && purchasedAddons.length > 0) {
        purchasedAddons.forEach((purchasedAddon) => {
          const addonType = getAddonTypeFromCode(purchasedAddon.code)
          if (
            addonType !== allAddonsTypes.ADDON_DOMAIN ||
            purchasedAddon.project_id
          ) {
            return
          }
          const addonDetail = getAddonByCode(
            availableAddons,
            purchasedAddon.code,
          )
          if (addonDetail) {
            maxAvailableAddonProject +=
              getAddonLimit(addonDetail) * purchasedAddon.quantity
          }
        })
      }
    }

    if (user && Object.entries(user.limits).length > 0)
      return {
        CRAWL_MAX_PAGES: user.limits.pages_to_crawl,
        MAX_PROJECTS_PER_USER: user.limits.projects,
        MAX_KEYWORD_LISTS_PER_USER: user.limits.keyword_lists,
        MAX_KEYWORDS_PER_LIST: user.limits.keywords_per_keyword_list,
        RANKING_UPDATE_FREQUENCY: user.limits.ranking_update_frequency,
        MAX_AVAILABLE_ADDON_PROJECTS: maxAvailableAddonProject,
        MAX_SUB_USER_COUNT: user.limits.users_per_account,
        KEYWORD_METRICS_UPDATE_LIMITS:
          user.limits.monthly_keyword_metrics_updates_limits,
        KEYWORD_METRICS_UPDATE_USED:
          user.limits.monthly_keyword_metrics_updates_used,
        AIW_WORDS_LIMITS: user.limits.aiw_words_limits,
        AIW_WORDS_USED: user.limits.aiw_words_used,
      }
  }

  return {
    CRAWL_MAX_PAGES: 150,
    MAX_PROJECTS_PER_USER: 1,
    MAX_KEYWORD_LISTS_PER_USER: 3,
    MAX_KEYWORDS_PER_LIST: 30,
    RANKING_UPDATE_FREQUENCY: 'weekly',
    MAX_AVAILABLE_ADDON_PROJECTS: maxAvailableAddonProject,
    MAX_SUB_USER_COUNT: 0,
    KEYWORD_METRICS_UPDATE_LIMITS: 0,
    KEYWORD_METRICS_UPDATE_USED: 0,
    AIW_WORDS_LIMITS: 0,
    AIW_WORDS_USED: 0,
  }
}

export const getMaxValuesForProject = (
  project = null,
  newAddonProject = false,
) => {
  if (isSignedIn()) {
    let domainAddon
    const availableAddons = getAvailableAddons() || null

    if (availableAddons)
      domainAddon = getAddonByKey(availableAddons, allAddonsTypes.ADDON_DOMAIN)

    const user = getLoggedUser()
    const projectLimits = project?.limits

    if (user && Object.entries(user.limits).length > 0)
      return {
        MAX_KEYWORDS_PER_PROJECT: newAddonProject
          ? domainAddon?.limits[
              addonMainLimitNames[allAddonsTypes.ADDON_TRACKEDKEYWORDS]
            ]
          : projectLimits?.keywords?.limit || user.limits.keywords_per_project,
        MAX_COMPETITORS_PER_PROJECT: newAddonProject
          ? domainAddon?.limits[
              addonMainLimitNames[allAddonsTypes.ADDON_COMPETITOR]
            ]
          : projectLimits?.competitors?.limit ||
            user.limits.competitors_per_project,
        MAX_COMPETITORS_LOCATIONS_PER_PROJECT:
          projectLimits?.locations?.limit || user.limits.locations_per_project,
        MAX_LOCATIONS_PER_PROJECT:
          projectLimits?.locations?.limit || user.limits.locations_per_project,
      }
  }

  return {
    MAX_KEYWORDS_PER_PROJECT: 25,
    MAX_COMPETITORS_PER_PROJECT: 2,
    MAX_COMPETITORS_LOCATIONS_PER_PROJECT: 1,
    MAX_LOCATIONS_PER_PROJECT: 1,
  }
}

export const checkAddonProject = (project_id) => {
  if (!isSignedIn() || !project_id) return false

  let isAddonProject = false

  const user = getLoggedUser()
  if (user.subscription) {
    const subscription = getUserSubscription() || null
    const purchasedAddons = subscription?.addOns

    if (purchasedAddons && purchasedAddons.length > 0)
      isAddonProject = some(
        purchasedAddons,
        (purchasedAddon) =>
          getAddonTypeFromCode(purchasedAddon.code) ===
            allAddonsTypes.ADDON_DOMAIN &&
          purchasedAddon.project_id === project_id,
      )
  }
  return isAddonProject
}

export const getUnblockCount = (
  page,
  feature,
  count,
  disableBlockFeature = false,
) => {
  if (disableBlockFeature) return count

  let userTier = getUserTier()
  if (isLimitDisabled()) {
    userTier = 'tier1'
  }
  const config = getConfig(userTier)

  return get(config, [page, feature], count)
}

export const getUnblockKeywordCount = (
  page,
  feature,
  count,
  disableBlockFeature = false,
) => {
  if (disableBlockFeature) return count

  const userTier = isLimitDisabled() ? 'tier1' : getUserTier()
  const config = getConfig(userTier)

  return get(config, [page, 'keywords', feature], count)
}

export const getblockCount = (
  page,
  feature,
  count,
  disableBlockFeature = false,
  filteredCount = -1,
) => {
  if (disableBlockFeature) return 0
  if (filteredCount >= 0 && !isPaidUser()) return count - filteredCount

  let userTier = getUserTier()
  if (isLimitDisabled()) {
    userTier = 'tier1'
  }
  const config = getConfig(userTier)

  return Math.max(0, count - get(config, [page, feature], count))
}

export const withDummyData = (
  data,
  scheme,
  dummyDataCount,
  forceUpdate = false,
) => {
  if (isPaidUser() && !forceUpdate) return data
  const newData = cloneDeep(data)

  for (let i = 0; i < dummyDataCount; i += 1) {
    newData.push(scheme)
  }
  return newData
}

export const shouldBlockPastDue = (user) => {
  if (!user || !user.subscription) return false
  const { subscriptionStatus } = user.subscription
  if (subscriptionStatus === 'past_due' && !isUserInGracePeriod(user)) {
    return true
  }
  return false
}

export const isUserInGracePeriod = (user) => {
  if (!user || !user.subscription) return false
  const { pastDueStartedAt, subscriptionStatus } = user.subscription
  if (subscriptionStatus === 'past_due') {
    // do something
    const past = moment(pastDueStartedAt)
    const now = moment()
    if (now.diff(past, 'hours') < 48) return true
  }
  return false
}
